import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createNews,
  deleteNews,
  getNews,
  updateNews,
  uploadImage,
} from "../../../../../services/newsServices";

import {
  buildActualitesStats,
  filterAndSortNews,
  isDemoUser,
  normalizeNewsPayload,
  safeJsonParse,
} from "../utils/actualitesUtils";

const INITIAL_FILTERS = {
  query: "",
  status: "all",
  sortOrder: "created_desc",
};

export default function useActualites() {
  const [news, setNews] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setUserInfo(user);
    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshNews = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getNews();
      setNews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des actualités :", error);
      toast.error("Erreur lors de la récupération des actualités.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshNews();
  }, [loadUserMode, refreshNews]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const uploadNewsImage = useCallback(async (imageFile) => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("category", "news");

    const uploadResponse = await uploadImage(formData);

    return (
      uploadResponse?.filePath ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      null
    );
  }, []);

  const buildPayloadWithImage = useCallback(
    async (formData) => {
      const payload = normalizeNewsPayload(formData);

      if (formData.imageFile) {
        const uploadedImageUrl = await uploadNewsImage(formData.imageFile);

        if (uploadedImageUrl) {
          payload.image_url = uploadedImageUrl;
        }
      }

      return payload;
    },
    [uploadNewsImage]
  );

  const filteredNews = useMemo(
    () => filterAndSortNews(news, filters),
    [news, filters]
  );

  const stats = useMemo(
    () => buildActualitesStats(news, filteredNews),
    [news, filteredNews]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedArticle(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (article) => {
      if (guardDemoMode()) return;

      setSelectedArticle(article);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((article) => {
    setSelectedArticle(article);
    setDrawerMode("details");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedArticle(null);
  }, []);

  const createArticle = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await createNews({
          ...payload,
          author_id: payload.author_id || userInfo?.id || userInfo?.userId || 2,
        });

        toast.success("Actualité créée avec succès.");
        closeDrawer();
        await refreshNews();
      } catch (error) {
        console.error("Erreur lors de la création de l'actualité :", error);
        toast.error("Erreur lors de la création de l'actualité.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshNews, userInfo]
  );

  const updateArticle = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await updateNews(payload);

        toast.success("Actualité modifiée avec succès.");
        closeDrawer();
        await refreshNews();
      } catch (error) {
        console.error("Erreur lors de la modification de l'actualité :", error);
        toast.error("Erreur lors de la modification de l'actualité.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshNews]
  );

  const askDelete = useCallback(
    (articleId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(articleId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (articleId) => {
      if (guardDemoMode()) return;

      try {
        await deleteNews(articleId);

        toast.success("Actualité supprimée avec succès.");
        setDeleteCandidateId(null);
        await refreshNews();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'actualité :", error);
        toast.error("Erreur lors de la suppression de l'actualité.");
      }
    },
    [guardDemoMode, refreshNews]
  );

  return {
    loading,
    saving,
    filters,
    setFilter,
    news,
    filteredNews,
    selectedArticle,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshNews,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      closeDrawer,
      createArticle,
      updateArticle,
      askDelete,
      cancelDelete,
      confirmDelete,
    },
  };
}